import { shuffleArray } from "@utils/arrayMethods";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { getSurveyOptionsWithTranslationKeys } from "src/pageDefinitions/actions/survey/util";
import { isUS } from "src/utils/userSegment";

export enum WdyhauOptionSet {
  baselineUS,
  baselineNonUS,
}

/**
 * Downstream Data team (Analytic Engineering / Data Engineering)
 * attributes these Wdyhau answers to HDYHAU channels.
 * Please contact the data team for ANY changes to be made on this ENUM.
 */
export enum WdyhauOption {
  amexrewards = "amexrewards",
  audio = "audio",
  blogOrReview = "blogOrReview",
  cashBackRewards = "cashBackRewards",
  coPromotionFromAnotherBrand = "coPromotionFromAnotherBrand",
  directMail = "directMail",
  discountAndDealSite = "discountAndDealSite",
  doctor = "doctor",
  facebook = "facebook",
  friendorfamily = "friendorfamily",
  helloFresh = "helloFresh",
  influencer = "influencer",
  instagram = "instagram",
  loyaltyAndPointsProgram = "loyaltyAndPointsProgram",
  newsPaperOrMagazine = "newsPaperOrMagazine",
  onlineTV = "onlineTV",
  other = "other",
  otherRadio = "otherRadio",
  pinterest = "pinterest",
  podcast = "podcast",
  posterOrBillboard = "posterOrBillboard",
  printMagazinePoster = "printMagazinePoster",
  publicRadio = "publicRadio",
  radio = "radio",
  reddit = "reddit",
  search = "search",
  siriusXM = "siriusXM",
  snapchat = "snapchat",
  streamingRadio = "streamingRadio",
  tiktok = "tiktok",
  tv = "tv",
  youtube = "youtube",
  zumba = "zumba",
}

export type WdyhauOptionProps = {
  set?: WdyhauOptionSet;
  exclude?: WdyhauOption[];
};

/**
 * Simplified WDYHAU answer set (MGWL-1183). Marketing Attribution aligned on
 * this reduced list of channels, and on passing the stable IDs through as the
 * answer value (rather than the user-facing copy). "other" is appended last in
 * getWdyhauOptions().
 */
const whereDidYouHear = {
  [WdyhauOptionSet.baselineUS]: [
    WdyhauOption.facebook,
    WdyhauOption.search,
    WdyhauOption.tv,
    WdyhauOption.audio,
    WdyhauOption.influencer,
    WdyhauOption.youtube,
    WdyhauOption.tiktok,
    WdyhauOption.pinterest,
    WdyhauOption.doctor,
    WdyhauOption.friendorfamily,
  ],
  [WdyhauOptionSet.baselineNonUS]: [
    WdyhauOption.facebook,
    WdyhauOption.search,
    WdyhauOption.tv,
    WdyhauOption.audio,
    WdyhauOption.influencer,
    WdyhauOption.youtube,
    WdyhauOption.tiktok,
    WdyhauOption.pinterest,
    WdyhauOption.doctor,
    WdyhauOption.friendorfamily,
  ],
};

let wdyhauOptionMemo: WdyhauOption[];

export function getWdyhauOptionProps(): WdyhauOptionProps {
  return {
    set: isUS() ? WdyhauOptionSet.baselineUS : WdyhauOptionSet.baselineNonUS,
    exclude: [],
  };
}

export function getWdyhauOptions({ set, exclude }: WdyhauOptionProps) {
  if (!wdyhauOptionMemo) {
    wdyhauOptionMemo = shuffleArray(
      whereDidYouHear[set ?? WdyhauOptionSet.baselineUS]
    );
    wdyhauOptionMemo.push(WdyhauOption.other);
    if (exclude) {
      wdyhauOptionMemo = wdyhauOptionMemo.filter((x) => !exclude.includes(x));
    }
  }
  return wdyhauOptionMemo;
}

export function getWdyhauSurveyOptions() {
  return getSurveyOptionsWithTranslationKeys(
    "whereDidYouHear",
    getWdyhauOptions(getWdyhauOptionProps())
  );
}

export function getWdyhauSurveyAnswersFromKey(
  key: WdyhauOption
): SurveyAnswersState {
  // Pass the stable option ID through as the answer for both whereDidYouHear
  // and whereDidYouHearKey (MGWL-1183). Previously whereDidYouHear stored the
  // user-facing copy, which Marketing Attribution asked us to stop doing.
  return { whereDidYouHear: [key], whereDidYouHearKey: [key] };
}
